import {ReactElement, useEffect, useRef} from 'react'
import {Trans, WithNamespaces, withNamespaces} from 'react-i18next'
import classNames from 'classnames'

import messages from '../assets/messages'
import {SumsubContainer, SumsubError} from './SumsubContainer'
import {HeldWithdrawalSummary, VideoVerificationScreen} from './utils'
import './video-verification.css'

type Message = {i18nKey: string; defaults: string}

type SumsubSession = {
  token: string
  locale: string
  fetchToken: () => Promise<string>
  onMessage: (type: string, payload: any) => void
  onError: (error: SumsubError) => void
}

type VideoVerificationDialogProps = WithNamespaces & {
  screen: VideoVerificationScreen
  summary: HeldWithdrawalSummary | null
  sumsub: SumsubSession | null
  isBusy?: boolean
  onStart: () => void
  onTryAgain: () => void
  onContactSupport: () => void
  /** Escape, the close button and every "later" button. The withdrawal stays on hold. */
  onClose: () => void
}

const Icon = ({name}: {name: string}) => (
  <span className="material-symbols-outlined" aria-hidden="true">
    {name}
  </span>
)

const Badge = ({icon, tone}: {icon: string; tone: 'blue' | 'ok' | 'warn' | 'err'}) => (
  <div className={`vv-badge vv-badge--${tone}`}>
    <Icon name={icon} />
  </div>
)

const Heading = ({title, text}: {title: Message; text?: Message}) => (
  <>
    <h2 className="vv-title" id="vv-title">
      <Trans {...title} />
    </h2>
    {text ? (
      <p className="vv-text">
        <Trans {...text} />
      </p>
    ) : null}
  </>
)

const Summary = ({summary, status}: {summary: HeldWithdrawalSummary; status: Message}) => (
  <div className="vv-summary" data-testid="vv-summary">
    <div className="vv-summary__row">
      <span>
        <Trans {...messages.videoVerificationSummaryWithdrawalLabel} />
      </span>
      <span>
        <Trans
          {...messages.videoVerificationSummaryWithdrawal}
          values={{amount: summary.amount, method: summary.method}}
        />
      </span>
    </div>
    <div className="vv-summary__row">
      <span>
        <Trans {...messages.videoVerificationSummaryFromLabel} />
      </span>
      <span>
        <Trans
          {...messages.videoVerificationSummaryAccount}
          values={{accountName: summary.accountName, login: summary.login}}
        />
      </span>
    </div>
    <div className="vv-summary__row">
      <span>
        <Trans {...messages.videoVerificationSummaryStatusLabel} />
      </span>
      <span>
        <Trans {...status} />
      </span>
    </div>
  </div>
)

type ActionProps = {message: Message; onClick: () => void; icon?: string; ghost?: boolean; disabled?: boolean}

const Action = ({message, onClick, icon, ghost, disabled}: ActionProps) => (
  <button
    type="button"
    className={classNames('vv-btn', {'vv-btn--ghost': ghost})}
    onClick={onClick}
    disabled={disabled}
  >
    {icon ? <Icon name={icon} /> : null}
    <Trans {...message} />
  </button>
)

const FOCUSABLE = 'button:not([disabled]), a[href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])'

/** Tab and Shift+Tab cycle inside the pop-up. */
const keepFocusInside = (event: KeyboardEvent, modal: HTMLElement | null) => {
  if (!modal) return
  const focusable = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE))
  if (!focusable.length) {
    event.preventDefault()
    return
  }
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement
  if (event.shiftKey && (active === first || active === modal)) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  } else if (!modal.contains(active)) {
    event.preventDefault()
    first.focus()
  }
}

function VideoVerificationDialogBase(props: VideoVerificationDialogProps): ReactElement {
  const {screen, summary, sumsub, isBusy, onStart, onTryAgain, onContactSupport, onClose, t} = props

  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'Tab') keepFocusInside(event, modalRef.current)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // The page below keeps still while the pop-up is open, and gets its focus back when it closes.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = overflow
      opener?.focus?.()
    }
  }, [])

  // A new screen replaces the button that had the focus, so the focus moves to the pop-up again.
  useEffect(() => {
    modalRef.current?.focus()
  }, [screen])

  const renderScreen = () => {
    switch (screen) {
      case 'sumsub':
        return (
          <>
            <Heading title={messages.videoVerificationSumsubTitle} text={messages.videoVerificationSumsubText} />
            {sumsub ? <SumsubContainer {...sumsub} /> : <div className="vv-sumsub" />}
            <div className="vv-actions">
              <Action ghost message={messages.videoVerificationCancelDoLater} onClick={onClose} />
            </div>
          </>
        )
      case 'checking':
        return (
          <>
            <Badge icon="hourglass_top" tone="blue" />
            <Heading title={messages.videoVerificationCheckingTitle} text={messages.videoVerificationCheckingText} />
            {summary ? <Summary summary={summary} status={messages.videoVerificationStatusInReview} /> : null}
            <div className="vv-actions">
              <Action message={messages.videoVerificationDone} onClick={onClose} />
            </div>
          </>
        )
      case 'retry':
        return (
          <>
            <Badge icon="refresh" tone="warn" />
            <Heading title={messages.videoVerificationRetryTitle} text={messages.videoVerificationRetryText} />
            <div className="vv-actions">
              <Action
                icon="videocam"
                message={messages.videoVerificationTryAgain}
                onClick={onTryAgain}
                disabled={isBusy}
              />
              <Action ghost message={messages.videoVerificationContactSupport} onClick={onContactSupport} />
            </div>
          </>
        )
      case 'locked':
        return (
          <>
            <Badge icon="schedule" tone="warn" />
            <Heading title={messages.videoVerificationLockedTitle} text={messages.videoVerificationLockedText} />
            <div className="vv-actions">
              <Action message={messages.videoVerificationContactSupport} onClick={onContactSupport} />
              <Action ghost message={messages.videoVerificationClose} onClick={onClose} />
            </div>
          </>
        )
      case 'rejected':
        return (
          <>
            <Badge icon="error" tone="err" />
            <Heading title={messages.videoVerificationRejectedTitle} text={messages.videoVerificationRejectedText} />
            {summary ? <Summary summary={summary} status={messages.videoVerificationStatusOnHold} /> : null}
            <div className="vv-actions">
              <Action message={messages.videoVerificationContactSupport} onClick={onContactSupport} />
              <Action ghost message={messages.videoVerificationClose} onClick={onClose} />
            </div>
          </>
        )
      case 'down':
        return (
          <>
            <Badge icon="cloud_off" tone="err" />
            <Heading title={messages.videoVerificationDownTitle} text={messages.videoVerificationDownText} />
            <div className="vv-actions">
              <Action message={messages.videoVerificationOk} onClick={onClose} />
            </div>
          </>
        )
      case 'required':
      default:
        return (
          <>
            <Badge icon="face" tone="blue" />
            <Heading title={messages.videoVerificationRequiredTitle} text={messages.videoVerificationRequiredText} />
            {summary ? <Summary summary={summary} status={messages.videoVerificationStatusWaiting} /> : null}
            <ol className="vv-steps">
              {[
                messages.videoVerificationStep1,
                messages.videoVerificationStep2,
                messages.videoVerificationStep3,
              ].map((step, index) => (
                <li key={step.i18nKey}>
                  <span className="vv-steps__n">{index + 1}</span>
                  <span>
                    <Trans {...step} />
                  </span>
                </li>
              ))}
            </ol>
            <div className="vv-actions">
              <Action
                icon="videocam"
                message={messages.videoVerificationStart}
                onClick={onStart}
                disabled={isBusy}
              />
              <Action ghost message={messages.videoVerificationDoItLater} onClick={onClose} />
            </div>
            <p className="vv-note">
              <Icon name="lock" />
              <span>
                <Trans {...messages.videoVerificationReserveNote} />
              </span>
            </p>
          </>
        )
    }
  }

  return (
    <div className={classNames('vv-scrim', {'vv-scrim--sumsub': screen === 'sumsub'})} data-testid="vv-scrim">
      <div className="vv-modal" role="dialog" aria-modal="true" aria-labelledby="vv-title" tabIndex={-1} ref={modalRef}>
        <button
          type="button"
          className="vv-close"
          aria-label={t(messages.videoVerificationClose.i18nKey, messages.videoVerificationClose.defaults)}
          onClick={onClose}
        >
          <Icon name="close" />
        </button>
        {renderScreen()}
      </div>
    </div>
  )
}

const VideoVerificationDialog = withNamespaces()(VideoVerificationDialogBase)

export {VideoVerificationDialog}
export type {SumsubSession, VideoVerificationDialogProps}
